import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import {
  Invoice,
  GetInvoicesResponse,
  GetAvailableContractsResponse,
  CreateInvoiceRequest,
  CreateInvoiceResponse,
  UpdateInvoiceRequest,
  UpdateInvoiceResponse,
  UpdateInvoiceStatusRequest,
} from "@/types/invoice";

class InvoiceAPIService {
  // Get available contracts for creating invoices
  static async getAvailableContracts(): Promise<GetAvailableContractsResponse> {
    return ApiService.request<GetAvailableContractsResponse>(EndpointKeys.GET_AVAILABLE_CONTRACTS);
  }

  // Get all invoices for the current contractor
  static async getInvoices(): Promise<GetInvoicesResponse> {
    return ApiService.request<GetInvoicesResponse>(EndpointKeys.GET_INVOICES);
  }

  // Get details for a specific invoice
  static async getInvoiceDetails(invoiceId: number): Promise<Invoice> {
    return ApiService.request<Invoice>(EndpointKeys.GET_INVOICE_DETAILS, {
      urlParams: { invoiceId },
    });
  }

  // Create a new invoice
  static async createInvoice(
    data: CreateInvoiceRequest,
  ): Promise<CreateInvoiceResponse> {
    return ApiService.request<CreateInvoiceResponse>(
      EndpointKeys.CREATE_INVOICE,
      {
        method: "POST",
        body: data,
      },
    );
  }

  // Update an existing invoice
  static async updateInvoice(
    invoiceId: number,
    data: UpdateInvoiceRequest,
  ): Promise<UpdateInvoiceResponse> {
    return ApiService.request<UpdateInvoiceResponse>(
      EndpointKeys.UPDATE_INVOICE,
      {
        method: "PUT",
        body: data,
        urlParams: { invoiceId },
      },
    );
  }

  // Update invoice status
  static async updateInvoiceStatus(
    invoiceId: number,
    data: UpdateInvoiceStatusRequest,
  ): Promise<Invoice> {
    return ApiService.request<Invoice>(EndpointKeys.UPDATE_INVOICE_STATUS, {
      method: "PATCH",
      body: data,
      urlParams: { invoiceId },
    });
  }

  // Submit an invoice (change status from DRAFT to SUBMITTED)
  static async submitInvoice(invoiceId: number): Promise<Invoice> {
    return ApiService.request<Invoice>(EndpointKeys.SUBMIT_INVOICE, {
      method: "POST",
      urlParams: { invoiceId },
    });
  }

  // Delete an invoice
  static async deleteInvoice(invoiceId: number): Promise<void> {
    return ApiService.request<void>(EndpointKeys.DELETE_INVOICE, {
      method: "DELETE",
      urlParams: { invoiceId },
    });
  }

  // Download invoice PDF document
  static async downloadInvoiceDocument(invoiceId: number): Promise<void> {
    return ApiService.downloadBlob(EndpointKeys.DOWNLOAD_INVOICE_DOCUMENT, {
      urlParams: { invoiceId },
    });
  }
}

export default InvoiceAPIService;
